/*
the maps for custom sort orders are used in the
custom sort functions below. e.g. EMER (emergency
tickets will show up at the top of the list)
*/

import { docLastUpdated } from '../helpers';

const sortByAlphanumericAscending = (a, b, docAttribute, fallbackAttribute) => {
  if (!a.doc[docAttribute] && !a.doc[fallbackAttribute]) {
    return +1;
  } else if (!b.doc[docAttribute] && !b.doc[fallbackAttribute]) {
    return -1;
  } else {
    return (a.doc[docAttribute] || a.doc[fallbackAttribute]).localeCompare(
      b.doc[docAttribute] || b.doc[fallbackAttribute]
    );
  }
};

const sortByAlphanumericDescending = (
  a,
  b,
  docAttribute,
  fallbackAttribute
) => {
  if (!a.doc[docAttribute] && !a.doc[fallbackAttribute]) {
    return +1;
  } else if (!b.doc[docAttribute] && !b.doc[fallbackAttribute]) {
    return -1;
  } else {
    return (b.doc[docAttribute] || b.doc[fallbackAttribute]).localeCompare(
      a.doc[docAttribute] || a.doc[fallbackAttribute]
    );
  }
};

const sortByWorkDate = (a, b, isDescending) => {
  const aDate = a.doc.fieldMeetScheduledDate
    ? a.doc.fieldMeetScheduledDate
    : a.doc.standbyScheduledDate
    ? a.doc.standbyScheduledDate
    : a.doc.pge_workDate;
  const bDate = b.doc.fieldMeetScheduledDate
    ? b.doc.fieldMeetScheduledDate
    : b.doc.standbyScheduledDate
    ? b.doc.standbyScheduledDate
    : b.doc.pge_workDate;
  if (!aDate) {
    return isDescending ? +1 : -1;
  } else if (!bDate) {
    return isDescending ? -1 : +1;
  } else {
    return isDescending
      ? bDate.localeCompare(aDate)
      : aDate.localeCompare(bDate);
  }
};

const sortByLastUpdated = (a, b, isDescending) => {
  const aDate = docLastUpdated(a);
  const bDate = docLastUpdated(b);
  if (!aDate) {
    return isDescending ? +1 : -1;
  } else if (!bDate) {
    return isDescending ? -1 : +1;
  } else {
    return isDescending
      ? bDate.localeCompare(aDate)
      : aDate.localeCompare(bDate);
  }
};

const sortByLanId = (a, b, isDescending) => {
  const aID = a.doc.metadata
    ? a.doc.metadata.originatingUser
      ? a.doc.metadata.originatingUser
      : a.doc.metadata.user
    : null;
  const bID = b.doc.metadata
    ? b.doc.metadata.originatingUser
      ? b.doc.metadata.originatingUser
      : b.doc.metadata.user
    : null;
  if (!aID) {
    return isDescending ? +1 : -1;
  } else if (!bID) {
    return isDescending ? -1 : +1;
  } else {
    return isDescending ? bID.localeCompare(aID) : aID.localeCompare(bID);
  }
};
// const sortByAddress = (a, b, option) => {
//   const streetNumberRegex = /[0-9]+.*?[0-9]+/g;
//   // if (a.doc.street) {
//   //   console.log(a.doc.street);
//   //   console.log(a.doc.pge_address);
//   // }

//   let sanitizedA = a.doc.pge_address;
//   let sanitizedB = b.doc.pge_address;

//   if (sanitizedA) sanitizedA = sanitizedA.replace(streetNumberRegex, '');
//   if (sanitizedB) sanitizedB = sanitizedB.replace(streetNumberRegex, '');
//   console.log(a.doc.street === undefined);
//   if (!a.doc.pge_address && !a.doc.street) {
//     return +1;
//   } else if (!b.doc.pge_address && !b.doc.street) {
//     return -1;
//   }
//   return option === 'ascending'
//     ? (sanitizedA || a.doc.street).localeCompare(sanitizedB || b.doc.street)
//     : (sanitizedB || b.doc.street).localeCompare(sanitizedA || a.doc.street);
// };

const prioritySortOrder = {
  EMER: 1,
  RUSH: 2,
  SHRT: 3,
  NORM: 4,
  STANDBY: 5,
  MEETUP: 6,
};

const sortByPriority = (a, b, isDescending) => {
  const priorityA = a.doc.priority
    ? a.doc.priority
    : a.doc.docType === 'fieldMeet'
    ? 'MEETUP'
    : 'STANDBY';
  const priorityB = b.doc.priority
    ? b.doc.priority
    : b.doc.docType === 'fieldMeet'
    ? 'MEETUP'
    : 'STANDBY';

  const sortOrderA = priorityA ? prioritySortOrder[priorityA] : 8;
  const sortOrderB = priorityB ? prioritySortOrder[priorityB] : 8;
  return isDescending ? sortOrderA - sortOrderB : sortOrderB - sortOrderA;
};

const statusSortOrder = {
  ONGOING: 1,
  OPEN: 2,
};

const sortByStatus = (a, b) =>
  statusSortOrder[a.doc.pge_status.toUpperCase()] -
    statusSortOrder[b.doc.pge_status.toUpperCase()] || 0;

const sortByLongDuration = (a, b, isDescending) => {
  const aDuration = !!a.doc?.durationModel?.prediction;
  const bDuration = !!b.doc?.durationModel?.prediction;

  if (!aDuration) {
    return isDescending ? +1 : -1;
  } else if (!bDuration) {
    return isDescending ? -1 : +1;
  }
};
const sortByDigIn = (a, b) => {
  const aDigIn = a.doc?.digInRiskModel?.prediction ? a.doc?.digInRiskModel?.prediction : 0;
  const bDigIn = b.doc?.digInRiskModel?.prediction ? b.doc?.digInRiskModel?.prediction : 0;

  if(aDigIn !== bDigIn){
    return bDigIn - aDigIn
  }else{
    return bDigIn - aDigIn
  }
};

export const sortDocsBy = (docs, sortCriterion) => {
  // Clone the array to avoid mutating the original
  const sortedDocs = [...docs];
  
  switch (sortCriterion) {
    case 'date-descending':
      return sortedDocs.sort((a, b) => sortByWorkDate(a, b, true));
    case 'date-ascending':
      return sortedDocs.sort((a, b) => sortByWorkDate(a, b, false));
    case 'updated-date-descending':
      return sortedDocs.sort((a, b) => sortByLastUpdated(a, b, true));
    case 'updated-date-ascending':
      return sortedDocs.sort((a, b) => sortByLastUpdated(a, b, false));
    case 'name-descending':
      return sortedDocs.sort((a, b) => sortByAlphanumericDescending(a, b, 'name'));
    case 'name-ascending':
      return sortedDocs.sort((a, b) => sortByAlphanumericAscending(a, b, 'name'));
    case 'work-type-descending':
      return sortedDocs.sort((a, b) =>
        sortByAlphanumericDescending(a, b, 'work_type')
      );
    case 'work-type-ascending':
      return sortedDocs.sort((a, b) =>
        sortByAlphanumericAscending(a, b, 'work_type')
      );
    case 'response-type-descending':
      return sortedDocs.sort((a, b) =>
        sortByAlphanumericDescending(a, b, 'pge_responseType')
      );
    case 'response-type-ascending':
      return sortedDocs.sort((a, b) =>
        sortByAlphanumericAscending(a, b, 'pge_responseType')
      );
    case 'address-descending':
      return sortedDocs.sort((a, b) =>
        sortByAlphanumericDescending(a, b, 'pge_address', 'street')
      );
    case 'address-ascending':
      return sortedDocs.sort((a, b) =>
        sortByAlphanumericAscending(a, b, 'pge_address', 'street')
      );
    case 'lan-id-ascending':
      return sortedDocs.sort((a, b) => sortByLanId(a, b, true));
    case 'lan-id-descending':
      return sortedDocs.sort((a, b) => sortByLanId(a, b, false));
    case 'city-descending':
      return sortedDocs.sort((a, b) => sortByAlphanumericDescending(a, b, 'place'));
    case 'city-ascending':
      return sortedDocs.sort((a, b) => sortByAlphanumericAscending(a, b, 'place'));
    case 'priority-ascending':
      return sortedDocs.sort((a, b) => sortByPriority(a, b, false));
    case 'priority-descending':
      return sortedDocs.sort((a, b) => sortByPriority(a, b, true));
    case 'status':
      return sortedDocs.sort(sortByStatus);
    case 'duration-ascending':
      return sortedDocs.sort((a, b) => sortByLongDuration(a, b, true));
    case 'duration-descending':
      return sortedDocs.sort((a, b) => sortByLongDuration(a, b, false));
    case 'digin-ascending':
      return sortedDocs.sort((a, b) => sortByDigIn(a, b));
    case 'digin-descending':
      return sortedDocs.sort((a, b) => sortByDigIn(b, a));
    default:
      return sortDocsBy(docs, 'date-descending');
  }
};
